import { Injectable } from '@angular/core';
import { PerformanceDetail, Product, ShareClass } from '@models';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { ProductsMapperParams } from '@products/utils/mappers/product.type.mapper';
import { SiteConfigService } from '@services';
import { ConfigurationId } from '@types';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('DataCheckService');

@Injectable({
  providedIn: 'root',
})
export class DataCheckService {
  public constructor(private siteConfig: SiteConfigService) {}

  public checkForMissingData(
    productsData: Product[],
    mapperParams: ProductsMapperParams
  ): void {
    logger.debug('checkForMissingData()', productsData, mapperParams);

    // for now, only run this on US
    // intl sites will probably need other cols checked
    if (!this.siteConfig.isSiteInternational()) {
      // check Avg Annual 1 year col. All product types except Interval Funds+VIP
      if (
        ![ConfigurationId.IF, ConfigurationId.VIP].includes(
          mapperParams.configurationName
        )
      ) {
        this.checkAvgAnnualData(mapperParams.configurationName, productsData);
      }

      switch (mapperParams.configurationName) {
        case ConfigurationId.MF:
        case ConfigurationId.TIF:
        case ConfigurationId.CIT:
          // check overall rating col for Mutual and Institutional (TIF) Funds
          this.checkRatingsOverallData(
            mapperParams.configurationName,
            productsData
          );
          break;
        case ConfigurationId.SMA:
          // check Cumulative 3 month for SMAs
          this.checkCumulative3MonthData(
            mapperParams.configurationName,
            productsData
          );
          break;
      }
    }
  }

  private checkAvgAnnualData = (
    configurationName: ConfigurationId,
    productsData: Product[]
  ): void => {
    logger.debug('checkAvgAnnualData()', configurationName);
    const shareClasses: ShareClass[] = this.getShareClasses(productsData);

    // Array.some() should be very efficient, as it returns true as soon as data found
    const hasData: boolean = shareClasses.some(
      (shareClass: ShareClass): boolean =>
        shareClass.performance?.monthEnd?.some(
          (detail: PerformanceDetail): boolean =>
            hasValue(detail, 'avgAnnual1YearReturn')
        ) ||
        shareClass.performance?.quarterEnd?.some(
          (detail: PerformanceDetail): boolean =>
            hasValue(detail, 'avgAnnual1YearReturn')
        )
    );
    if (!hasData) {
      this.logIssue(
        'Missing PDS Data. All funds missing: avgAnnual1YearReturn',
        configurationName,
        productsData
      );
    }
  };

  private checkCumulative3MonthData = (
    configurationName: ConfigurationId,
    productsData: Product[]
  ): void => {
    logger.debug('checkCumulative3MonthData()', configurationName);
    const shareClasses: ShareClass[] = this.getShareClasses(productsData);

    // Array.some() should be very efficient, as it returns true as soon as data found
    const hasData: boolean = shareClasses.some(
      (shareClass: ShareClass): boolean =>
        shareClass.performance?.monthEnd?.some(
          (detail: PerformanceDetail): boolean =>
            hasValue(detail, 'cummulativeReturn3Month')
        ) ||
        shareClass.performance?.quarterEnd?.some(
          (detail: PerformanceDetail): boolean =>
            hasValue(detail, 'cummulativeReturn3Month')
        )
    );
    if (!hasData) {
      this.logIssue(
        'Missing PDS Data. All funds missing: cummulativeReturn3Month',
        configurationName,
        productsData
      );
    }
  };

  private checkRatingsOverallData = (
    configurationName: ConfigurationId,
    productsData: Product[]
  ): void => {
    logger.debug('checkRatingsOverallData()', configurationName);
    const shareClasses: ShareClass[] = this.getShareClasses(productsData);

    // Array.some() should be very efficient, as it returns true as soon as data found
    const hasData: boolean = shareClasses.some(
      (shareClass: ShareClass): boolean =>
        shareClass.ratings &&
        hasValue(shareClass.ratings, 'morningStarRatingOverall')
    );
    if (!hasData) {
      this.logIssue(
        'Missing PDS Data. All funds missing: morningStarRatingOverall',
        configurationName,
        productsData
      );
    }
  };

  private getShareClasses = (productsData: Product[]): ShareClass[] =>
    productsData.flatMap(
      (product: Product): ShareClass[] => product.shareClasses
    );

  // NB: logger is wrapped in a method so it can be mocked in unit tests
  // "warn" level will record this in DataDog on production
  private logIssue = (
    msg: string,
    configurationName: ConfigurationId,
    productsData: Product[]
  ): void => logger.warn(msg, configurationName, productsData);
}
